import { Injectable } from '@angular/core';
import { ActionInBookingType, TicketAirType } from "@afidium/ts-facade";
import _cloneDeep from "lodash-es/cloneDeep";
import { ISortOutput, SortDirection } from "libraries/interfaces/ISort";
import * as moment from "moment";
import { ComputeValuePipe, EConfigurableTableFields, IValueType } from "libraries/pipes/configurable-table/compute-value.pipe";
import { EFixedColumn } from "./ticketing-table.component";

@Injectable()
export class TicketingTableHelperService {

    constructor(
        private readonly computeStringValuePipe: ComputeValuePipe,
    ) {
    }

    public divideLineByAction(ticketArray: TicketAirType[]): TicketAirType[] {
        const output: TicketAirType[] = [];
        (ticketArray || [])
            .forEach(
                (ticket: TicketAirType): void => {

                    if (!ticket.actionArray?.length) {
                        output.push(ticket);
                        // @ts-ignore
                        output.push({});
                    }

                    ticket.actionArray.forEach(
                        (action: ActionInBookingType, index: number): void => {
                            const ticketClone: TicketAirType = _cloneDeep(ticket);
                            ticketClone.actionArray = [ticketClone.actionArray[index]];
                            output.push(ticketClone);
                            // @ts-ignore
                            output.push({});
                        }
                    );
                }
            );
        return output;
    }

    public sort(ticketArray: TicketAirType[], sortOutput: ISortOutput): TicketAirType[] {
        const sortedByDate: TicketAirType[] = ticketArray
            .sort((a: TicketAirType, b: TicketAirType): number => this.compareDate(a, b, SortDirection.descending));

        return sortedByDate
            .sort((a: TicketAirType, b: TicketAirType): number => {
                switch (sortOutput.actionName) {
                    case EConfigurableTableFields.BEGIN:
                        return this.compareBegin(a, b, sortOutput.direction);
                    case EFixedColumn.ticketNumber:
                        return this.compareTicketNumber(a, b, sortOutput.direction);
                    case EFixedColumn.system:
                        return this.compareSystem(a, b, sortOutput.direction);
                    case EFixedColumn.type:
                        return this.compareType(a, b, sortOutput.direction);
                    case EFixedColumn.purpose:
                        return this.compareFoldAction(a, b, sortOutput.direction);
                    case EConfigurableTableFields.BOOKING:
                        return this.compareBooking(a, b, sortOutput.direction, EConfigurableTableFields.BOOKING);
                    case EConfigurableTableFields.GDS_BOOKING:
                        return this.compareBooking(a, b, sortOutput.direction, EConfigurableTableFields.GDS_BOOKING);
                    case EConfigurableTableFields.COST_TOTAL:
                        return this.comparePrice(a, b, sortOutput.direction);
                    case EConfigurableTableFields.UP:
                        return this.compareUp(a, b, sortOutput.direction);
                    case EConfigurableTableFields.AUTHOR:
                        return this.compareAuthor(a, b, sortOutput.direction);
                    case EConfigurableTableFields.TRAVELLER:
                        return this.compareTraveller(a, b, sortOutput.direction);
                    case 'eventDate':
                    default:
                        return this.compareDate(a, b, sortOutput.direction);
                }
            });
    }

    private compareUp(a: TicketAirType, b: TicketAirType, direction: SortDirection): number {
        return this.compareNumber(a.sectorsCredited, b.sectorsCredited, direction);
    }
    private compareDate(a: TicketAirType, b: TicketAirType, direction: SortDirection): number {
        if (moment(a.actionArray[0].when.value).isBefore(moment(b.actionArray[0].when.value))) {
            return direction === SortDirection.ascending ? -1 : 1;
        }
        return direction === SortDirection.ascending ? 1 : -1;
    }

    private compareBooking(a: TicketAirType, b: TicketAirType, direction: SortDirection, field: EConfigurableTableFields): number {
        const lastValue : string = direction === 'ascending' ?  "zzzzzzzzzzzzzzzzzz" : '0';
        const aValue: IValueType = this.computeStringValuePipe.transform(a, field);
        const bValue: IValueType = this.computeStringValuePipe.transform(b, field);

        return this.compareString(
            aValue?.value ? (aValue.value as string) : lastValue,
            bValue?.value ? (bValue.value as string) : lastValue,
            direction);
    }
    private compareTicketNumber(a: TicketAirType, b: TicketAirType, direction: SortDirection): number {
        return this.compareString(
            a.number,
            b.number,
            direction);
    }
    private compareSystem(a: TicketAirType, b: TicketAirType, direction: SortDirection): number {
        const lastValue : string = direction === 'ascending' ?  "zzzzzzzzzzzzzzzzzz" : '0';
        const aValue : string = a.host?.code ? a.host.code : lastValue;
        const bValue : string = b.host?.code ? b.host.code : lastValue;
        return this.compareString(
            aValue,
            bValue,
            direction);
    }
    private compareType(a: TicketAirType, b: TicketAirType, direction: SortDirection): number {
        const lastValue : string = direction === 'ascending' ?  "zzzzzzzzzzzzzzzzzz" : '0';
        const aValue : string = a.xIssue ? a.xIssue : lastValue;
        const bValue : string = b.xIssue ? b.xIssue : lastValue;
        return this.compareString(
            aValue,
            bValue,
            direction);
    }
    private compareFoldAction(a: TicketAirType, b: TicketAirType, direction: SortDirection): number {
        const lastValue : string = direction === 'ascending' ?  "zzzzzzzzzzzzzzzzzz" : '0';
        const aValue : string = a.actionArray?.length && a.actionArray[0].xPurpose ?  a.actionArray[0].xPurpose : lastValue;
        const bValue : string = b.actionArray?.length && b.actionArray[0].xPurpose ?  b.actionArray[0].xPurpose : lastValue;
        return this.compareString(aValue, bValue, direction);
    }
    private compareBegin(a: TicketAirType, b: TicketAirType, direction: SortDirection): number {
        const lastValue : string = direction === 'ascending' ?  "zzzzzzzzzzzzzzzzzz" : '0';
        const aValue: string = a.begin?.value ? a.begin.value : lastValue;
        const bValue: string = b.begin?.value ? b.begin.value : lastValue;
        return this.compareString(aValue, bValue, direction);
    }

    private compareTraveller(a: TicketAirType, b: TicketAirType, direction: SortDirection): number {
        return this.compareString(a.traveller.name, b.traveller.name, direction);
    }

    private compareAuthor(a: TicketAirType, b: TicketAirType, direction: SortDirection): number {
        const lastValue : string = direction === 'ascending' ?  "zzzzzzzzzzzzzzzzzz" : '0';
        const aValue : string = a.actionArray?.length && a.actionArray[0].who ?  a.actionArray[0].who : lastValue;
        const bValue : string = b.actionArray?.length && b.actionArray[0].who ?  b.actionArray[0].who : lastValue;
        return this.compareString(aValue, bValue, direction);
    }

    private comparePrice(a: TicketAirType, b: TicketAirType, direction: SortDirection): number {
        const aValue: IValueType = this.computeStringValuePipe.transform(a, EConfigurableTableFields.COST_TOTAL);
        const bValue: IValueType = this.computeStringValuePipe.transform(b, EConfigurableTableFields.COST_TOTAL);
        const lastValue : number = direction === 'ascending' ?  99999999999 : 0;
        return this.compareNumber(
            aValue?.price?.amount ? aValue.price.amount : lastValue,
            bValue?.price?.amount ? bValue.price.amount : lastValue,
            direction
        );
    }

    private compareString(a: string, b: string, direction: SortDirection): number {
        if (a.localeCompare(b) < 0) {
            return direction === SortDirection.ascending ? -1 : 1;
        }
        return direction === SortDirection.ascending ? 1 : -1;
    }

    private compareNumber(a: number, b: number, direction: SortDirection): number {
        // console.log(a, b)
        if (direction === SortDirection.ascending) {
            return a > b ? 1 : a < b ? -1 : 0;
        }
        return a < b ? 1 : a > b ? -1 : 0;
    }
}
